import { ArchitectureParseError } from "../errors";
import type { FlowStep } from "../types";
import { asString } from "./parse-bullets-v3";

// Coerce js-yaml output for a v0.3 flow's `steps:` block into structured
// FlowStep[]. Mirrors parseRisks: forgiving where it can be (descriptions
// optional, defaulting to `<source> -> <target>` so the renderer always
// has something to show) and strict where it has to be (source / target
// must be non-empty — without them the step is not a real edge).

interface ParseStepContext {
  /** Path-like label for error messages, e.g. "L2.DataFlows.checkout". */
  scope: string;
  /**
   * When true, malformed steps throw. v0.3 callers pass strict=true; a
   * lenient mode is reserved for callers that want to keep parsing past
   * one bad entry.
   */
  strict: boolean;
}

export function parseFlowSteps(value: unknown, ctx: ParseStepContext): FlowStep[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return [];
  const out: FlowStep[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const entry = value[i];
    const step = coerceStep(entry, out.length + 1, {
      ...ctx,
      scope: `${ctx.scope}.steps[${i}]`,
    });
    if (step) out.push(step);
  }
  return out;
}

function coerceStep(value: unknown, number: number, ctx: ParseStepContext): FlowStep | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: expected a step object with at least \`source\` and \`target\`.`,
      );
    }
    return null;
  }
  const rec = value as Record<string, unknown>;
  const source = asString(rec.source) ?? "";
  const target = asString(rec.target) ?? "";
  if (source === "" || target === "") {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: each step must have non-empty \`source\` and \`target\` slugs.`,
      );
    }
    return null;
  }
  const description =
    asString(rec.what_data) ?? asString(rec.description) ?? `${source} → ${target}`;
  return { number, source, target, description };
}
